import { useState, useEffect, useRef } from 'react';
import API from '../utils/API';

const EMPTY_PROFILE = {
  first_name: '', last_name: '', work_email: '', personal_email: '',
  mobile_phone: '', other_phone: '', pronouns: '', lobby_filer_id: '',
  user_bio: '', linkedin_url: '', facebook_url: '', twitter_url: '',
  instagram_url: '', youtube_url: '', threads_url: '', tiktok_url: '', bluesky_url: '',
};

const EMPTY_JOB = {
  title: '', start_date: '', organization_id: '', division_id: '',
  street_address: '', suite_number: '', city: '', state: 'CA', zip_code: '', work_phone: '',
};

export default function AdminCreateProfile() {
  const [profile, setProfile] = useState({ ...EMPTY_PROFILE });
  const [addJob, setAddJob] = useState(true);
  const [job, setJob] = useState({ ...EMPTY_JOB });
  const [showSocials, setShowSocials] = useState(false);

  // Org typeahead
  const [orgSearch, setOrgSearch] = useState('');
  const [orgResults, setOrgResults] = useState([]);
  const [orgSearching, setOrgSearching] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const orgRef = useRef(null);

  // Divisions
  const [divisions, setDivisions] = useState([]);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const setP = (field, val) => setProfile(p => ({ ...p, [field]: val }));
  const setJ = (field, val) => setJob(j => ({ ...j, [field]: val }));

  // Close org dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (orgRef.current && !orgRef.current.contains(e.target)) setShowOrgDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced org search
  useEffect(() => {
    if (orgSearch.length < 2 || selectedOrg) {
      setOrgResults([]);
      setShowOrgDropdown(false);
      return;
    }
    const t = setTimeout(() => {
      setOrgSearching(true);
      API.searchOrganizations(orgSearch)
        .then(r => { setOrgResults(r.data); setShowOrgDropdown(r.data.length > 0); })
        .catch(() => {})
        .finally(() => setOrgSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [orgSearch, selectedOrg]);

  const selectOrg = (org) => {
    setOrgSearch(org.organization_name);
    setShowOrgDropdown(false);
    setOrgResults([]);
    setJob(j => ({ ...j, organization_id: org.organization_id, division_id: '' }));
    setDivisions([]);

    Promise.all([
      API.getSingleOrganizations(org.organization_id),
      API.getDivisionsByOrgId(org.organization_id),
    ]).then(([orgRes, divsRes]) => {
      const full = orgRes.data;
      setSelectedOrg(full);
      setJob(j => ({
        ...j,
        street_address: full.street_address || '',
        suite_number: full.suite_number || '',
        city: full.city || '',
        state: full.state || 'CA',
        zip_code: full.zip_code || '',
        work_phone: full.main_phone || '',
      }));
      setDivisions(divsRes.data);
    }).catch(() => {});
  };

  const clearOrg = () => {
    setSelectedOrg(null);
    setOrgSearch('');
    setOrgResults([]);
    setShowOrgDropdown(false);
    setDivisions([]);
    setJob(j => ({ ...j, organization_id: '', division_id: '', street_address: '', suite_number: '', city: '', state: 'CA', zip_code: '', work_phone: '' }));
  };

  const handleDivisionChange = (divId) => {
    setJ('division_id', divId);
    if (!divId) {
      if (selectedOrg) {
        setJob(j => ({ ...j, division_id: '', street_address: selectedOrg.street_address || '', suite_number: selectedOrg.suite_number || '', city: selectedOrg.city || '', state: selectedOrg.state || 'CA', zip_code: selectedOrg.zip_code || '', work_phone: selectedOrg.main_phone || '' }));
      }
      return;
    }
    const div = divisions.find(d => String(d.division_id) === String(divId));
    if (div) {
      setJob(j => ({
        ...j,
        division_id: divId,
        street_address: div.street_address || selectedOrg?.street_address || '',
        suite_number: div.suite_number || selectedOrg?.suite_number || '',
        city: div.city || selectedOrg?.city || '',
        state: div.state || selectedOrg?.state || 'CA',
        zip_code: div.zip_code || selectedOrg?.zip_code || '',
        work_phone: div.division_phone || selectedOrg?.main_phone || '',
      }));
    }
  };

  const resetForm = () => {
    setProfile({ ...EMPTY_PROFILE });
    setJob({ ...EMPTY_JOB });
    setAddJob(true);
    setOrgSearch('');
    setSelectedOrg(null);
    setDivisions([]);
    setShowSocials(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await API.adminCreateProfile({ profile, job: addJob ? job : null });
      setSuccess(`Profile created for ${res.data.name}. Profile ID: ${res.data.profile_id}`);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to create profile.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="acp-form" onSubmit={handleSubmit}>
      {success && (
        <div className="acp-banner acp-banner-success">
          <i className="bi bi-check-circle-fill me-2" />{success}
          <button type="button" className="acp-banner-dismiss" onClick={() => setSuccess(null)}>
            <i className="bi bi-x" />
          </button>
        </div>
      )}
      {error && (
        <div className="acp-banner acp-banner-error">
          <i className="bi bi-exclamation-circle me-2" />{error}
          <button type="button" className="acp-banner-dismiss" onClick={() => setError(null)}>
            <i className="bi bi-x" />
          </button>
        </div>
      )}

      {/* ── Profile Info ── */}
      <div className="acp-section">
        <h3 className="acp-section-title"><i className="bi bi-person me-2" />Profile Information</h3>

        <div className="acp-grid-2">
          <div className="acp-field">
            <label className="acp-label">First Name <span className="acp-req">*</span></label>
            <input className="acp-input" value={profile.first_name} onChange={e => setP('first_name', e.target.value)} required />
          </div>
          <div className="acp-field">
            <label className="acp-label">Last Name <span className="acp-req">*</span></label>
            <input className="acp-input" value={profile.last_name} onChange={e => setP('last_name', e.target.value)} required />
          </div>
        </div>

        <div className="acp-grid-2">
          <div className="acp-field">
            <label className="acp-label">Work Email <span className="acp-req">*</span></label>
            <input className="acp-input" type="email" value={profile.work_email} onChange={e => setP('work_email', e.target.value)} required />
          </div>
          <div className="acp-field">
            <label className="acp-label">Personal Email <span className="acp-opt">optional</span></label>
            <input className="acp-input" type="email" value={profile.personal_email} onChange={e => setP('personal_email', e.target.value)} />
          </div>
        </div>

        <div className="acp-grid-2">
          <div className="acp-field">
            <label className="acp-label">Mobile Phone <span className="acp-opt">optional</span></label>
            <input className="acp-input" type="tel" value={profile.mobile_phone} onChange={e => setP('mobile_phone', e.target.value)} />
          </div>
          <div className="acp-field">
            <label className="acp-label">Other Phone <span className="acp-opt">optional</span></label>
            <input className="acp-input" type="tel" value={profile.other_phone} onChange={e => setP('other_phone', e.target.value)} />
          </div>
        </div>

        <div className="acp-grid-2">
          <div className="acp-field">
            <label className="acp-label">Pronouns <span className="acp-opt">optional</span></label>
            <input className="acp-input" placeholder="e.g. they/them" value={profile.pronouns} onChange={e => setP('pronouns', e.target.value)} />
          </div>
          <div className="acp-field">
            <label className="acp-label">Cal-Access Lobby Filer ID <span className="acp-opt">optional</span></label>
            <input className="acp-input" value={profile.lobby_filer_id} onChange={e => setP('lobby_filer_id', e.target.value)} />
          </div>
        </div>

        <div className="acp-field">
          <label className="acp-label">Bio <span className="acp-opt">optional</span></label>
          <textarea className="acp-textarea" rows={3} value={profile.user_bio} onChange={e => setP('user_bio', e.target.value)} />
        </div>

        {/* Social links — collapsible */}
        <button type="button" className="acp-toggle-socials" onClick={() => setShowSocials(s => !s)}>
          <i className={`bi bi-chevron-${showSocials ? 'up' : 'down'} me-1`} />
          Social Links
          {showSocials ? '' : <span className="acp-opt ms-1">optional</span>}
        </button>
        {showSocials && (
          <div className="acp-socials-grid">
            {[
              ['linkedin_url', 'bi-linkedin', 'LinkedIn'],
              ['facebook_url', 'bi-facebook', 'Facebook'],
              ['twitter_url', 'bi-twitter-x', 'Twitter / X'],
              ['instagram_url', 'bi-instagram', 'Instagram'],
              ['youtube_url', 'bi-youtube', 'YouTube'],
              ['threads_url', 'bi-threads', 'Threads'],
              ['tiktok_url', 'bi-tiktok', 'TikTok'],
              ['bluesky_url', 'bi-bluesky', 'Bluesky'],
            ].map(([field, icon, label]) => (
              <div key={field} className="acp-field">
                <label className="acp-label"><i className={`bi ${icon} me-1`} />{label}</label>
                <input className="acp-input" type="url" value={profile[field]} onChange={e => setP(field, e.target.value)} placeholder="https://" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Job Experience ── */}
      <div className="acp-section">
        <div className="acp-section-head">
          <h3 className="acp-section-title"><i className="bi bi-briefcase me-2" />Job Experience</h3>
          <label className="acp-checkbox-label">
            <input type="checkbox" checked={addJob} onChange={e => setAddJob(e.target.checked)} />
            Add current job
          </label>
        </div>

        {addJob && (
          <>
            <div className="acp-grid-2">
              <div className="acp-field">
                <label className="acp-label">Title <span className="acp-req">*</span></label>
                <input className="acp-input" value={job.title} onChange={e => setJ('title', e.target.value)} required={addJob} />
              </div>
              <div className="acp-field">
                <label className="acp-label">Start Date <span className="acp-req">*</span></label>
                <input className="acp-input" type="date" value={job.start_date} onChange={e => setJ('start_date', e.target.value)} required={addJob} />
              </div>
            </div>

            <div className="acp-grid-2">
              {/* Organization typeahead */}
              <div className="acp-field">
                <label className="acp-label">Organization <span className="acp-req">*</span></label>
                <div className="acp-typeahead" ref={orgRef}>
                  <div className="acp-typeahead-wrap">
                    <input
                      className="acp-input"
                      placeholder="Search organizations…"
                      value={orgSearch}
                      onChange={e => {
                        setOrgSearch(e.target.value);
                        if (selectedOrg) clearOrg();
                      }}
                      onFocus={() => orgResults.length > 0 && setShowOrgDropdown(true)}
                      autoComplete="off"
                      required={addJob && !job.organization_id}
                    />
                    {orgSearching && <span className="acp-spinner" />}
                    {selectedOrg && (
                      <button type="button" className="acp-typeahead-clear" onClick={clearOrg} title="Clear">
                        <i className="bi bi-x-lg" />
                      </button>
                    )}
                  </div>
                  {showOrgDropdown && orgResults.length > 0 && (
                    <div className="acp-typeahead-dropdown">
                      {orgResults.map(org => (
                        <button
                          type="button"
                          key={org.organization_id}
                          className="acp-typeahead-item"
                          onMouseDown={() => selectOrg(org)}
                        >
                          <span className="acp-typeahead-name">{org.organization_name}</span>
                          {org.group_type && <span className="acp-typeahead-meta">{org.group_type}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Division select */}
              <div className="acp-field">
                <label className="acp-label">Division <span className="acp-opt">optional</span></label>
                <select
                  className="acp-select"
                  value={job.division_id}
                  onChange={e => handleDivisionChange(e.target.value)}
                  disabled={!selectedOrg}
                >
                  <option value="">{selectedOrg ? (divisions.length ? '— None —' : 'No divisions') : 'Select org first'}</option>
                  {divisions.map(d => (
                    <option key={d.division_id} value={d.division_id}>{d.division_name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Address (auto-filled from org/division) */}
            <div className="acp-address-block">
              <p className="acp-address-note">
                <i className="bi bi-info-circle me-1" />
                Address auto-filled from the selected organization or division — edit as needed.
              </p>
              <div className="acp-grid-2">
                <div className="acp-field">
                  <label className="acp-label">Street Address</label>
                  <input className="acp-input" value={job.street_address} onChange={e => setJ('street_address', e.target.value)} />
                </div>
                <div className="acp-field">
                  <label className="acp-label">Suite / Unit</label>
                  <input className="acp-input" value={job.suite_number} onChange={e => setJ('suite_number', e.target.value)} />
                </div>
              </div>
              <div className="acp-grid-3">
                <div className="acp-field">
                  <label className="acp-label">City</label>
                  <input className="acp-input" value={job.city} onChange={e => setJ('city', e.target.value)} />
                </div>
                <div className="acp-field">
                  <label className="acp-label">State</label>
                  <input className="acp-input" value={job.state} onChange={e => setJ('state', e.target.value)} maxLength={2} />
                </div>
                <div className="acp-field">
                  <label className="acp-label">Zip Code</label>
                  <input className="acp-input" value={job.zip_code} onChange={e => setJ('zip_code', e.target.value)} />
                </div>
              </div>
              <div className="acp-field" style={{ maxWidth: '260px' }}>
                <label className="acp-label">Work Phone</label>
                <input className="acp-input" type="tel" value={job.work_phone} onChange={e => setJ('work_phone', e.target.value)} />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="acp-footer">
        <button type="button" className="acp-btn-ghost" onClick={resetForm} disabled={submitting}>
          Clear Form
        </button>
        <button type="submit" className="acp-btn-primary" disabled={submitting}>
          {submitting ? <><i className="bi bi-arrow-repeat admin-spin me-1" />Creating…</> : 'Create Profile'}
        </button>
      </div>
    </form>
  );
}
